import { useState, useEffect } from "react";
import { useLoading } from "../context/LoadingContext";
import art1 from "../svg/art1.svg";
import art2 from "../svg/art2.svg";
import art3 from "../svg/art3.svg";
import art4 from "../svg/art4.svg";
import art5 from "../svg/art5.svg";

const artSvgs = [art1, art2, art3, art4, art5];

export default function Loading() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const { isLoading, setIsLoading } = useLoading();
  const [startTime] = useState(Date.now());
  const [isContentReady, setIsContentReady] = useState(false);
  const MIN_DISPLAY_TIME = 2000; // Minimum 2 seconds to appreciate the loader

  useEffect(() => {
    // Cycle through art SVGs with slower timing and fade effect for better visibility
    const interval = setInterval(() => {
      // Fade out
      setOpacity(0);
      // After fade out, change image and fade in
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % artSvgs.length);
        setOpacity(1);
      }, 150); // Half of the total cycle time for fade
    }, 300); // Slowed down to 300ms so each SVG is more visible (was 220ms)

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Check if page is fully loaded
    const checkPageLoaded = () => {
      const imagesLoaded =
        document.images.length === 0 ||
        Array.from(document.images).every((img) => img.complete);

      const videosLoaded =
        document.querySelectorAll("video").length === 0 ||
        Array.from(document.querySelectorAll("video")).every(
          (video) => video.readyState >= 3
        );

      if (document.readyState === "complete" && imagesLoaded && videosLoaded) {
        setIsContentReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    checkPageLoaded();

    // Also listen for load event
    const handleLoad = () => {
      checkPageLoaded();
    };
    window.addEventListener("load", handleLoad);

    // Check periodically as fallback
    const checkInterval = setInterval(() => {
      checkPageLoaded();
    }, 100);

    return () => {
      window.removeEventListener("load", handleLoad);
      clearInterval(checkInterval);
    };
  }, []);

  // Ensure minimum display time before hiding loader
  useEffect(() => {
    if (isContentReady) {
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);

      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, remainingTime + 200); // Small buffer for smooth transition

      return () => clearTimeout(timeout);
    }
  }, [isContentReady, startTime, setIsLoading]);

  if (!isLoading) return null;

  return (
    <div
      className={`fixed inset-0 bg-black z-[9999] flex items-center justify-center transition-opacity duration-700 ${
        isLoading ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="flex items-center justify-center">
        <img
          src={artSvgs[currentIndex]}
          alt="Loading"
          className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 transition-opacity duration-150"
          style={{ opacity }}
        />
      </div>
    </div>
  );
}
